import { useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Activity,
  Users,
  Settings,
  LogOut,
  Shield,
  Menu,
  X,
  Bell,
  ChevronDown,
  FileText,
  BarChart3,
  Package,
  Globe,
  Key,
  DollarSign,
  Cpu,
  Bot,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/cn';
import { useAdminAuth } from '@/services/adminAuth';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

const navigation: NavItem[] = [
  { label: 'Dashboard', href: '/admin/dashboard', icon: <LayoutDashboard size={20} /> },
  { label: 'Status Monitor', href: '/admin/status', icon: <Activity size={20} /> },
  { label: 'Analytics', href: '/admin/analytics', icon: <BarChart3 size={20} /> },
  { label: 'AI Models', href: '/admin/ai-models', icon: <Bot size={20} /> },
  { label: 'API Providers', href: '/admin/api-providers', icon: <Cpu size={20} /> },
  { label: 'Pricing', href: '/admin/pricing', icon: <DollarSign size={20} /> },
  { label: 'Users', href: '/admin/users', icon: <Users size={20} /> },
  { label: 'Extensions', href: '/admin/extensions', icon: <Package size={20} /> },
  { label: 'Content', href: '/admin/content', icon: <FileText size={20} /> },
  { label: 'Settings', href: '/admin/settings', icon: <Settings size={20} /> },
];

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, isAuthenticated, checkSession, logout } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Check authentication on mount and periodically
  useEffect(() => {
    if (!isAuthenticated || !checkSession()) {
      navigate('/admin/login', { state: { from: location.pathname }, replace: true });
    }
  }, [isAuthenticated, checkSession, navigate, location.pathname]);

  // Check session every minute
  useEffect(() => {
    const interval = setInterval(() => {
      if (!checkSession()) {
        navigate('/admin/login', { replace: true });
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [checkSession, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  if (!isAuthenticated || !session) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-bg-base">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full bg-bg-card border-r border-border-default transition-all duration-300",
          sidebarOpen ? "w-64" : "w-20",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-border-default">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            {sidebarOpen && (
              <div>
                <span className="font-bold text-text-primary">Ochtopus</span>
                <span className="text-xs text-text-tertiary block">Admin Panel</span>
              </div>
            )}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg hover:bg-bg-elevated transition-colors"
          >
            <Menu size={18} className="text-text-secondary" />
          </button>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg hover:bg-bg-elevated transition-colors"
          >
            <X size={18} className="text-text-secondary" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href ||
              (item.href !== '/admin/dashboard' && location.pathname.startsWith(item.href));

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive
                    ? "bg-accent/10 text-accent"
                    : "text-text-secondary hover:text-text-primary hover:bg-bg-elevated"
                )}
              >
                {item.icon}
                {sidebarOpen && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {item.badge !== undefined && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-accent text-white">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border-default">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
          >
            <Globe size={20} />
            {sidebarOpen && <span>View Website</span>}
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors mt-1"
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={cn(
        "transition-all duration-300",
        sidebarOpen ? "lg:ml-64" : "lg:ml-20"
      )}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 bg-bg-card border-b border-border-default flex items-center justify-between px-4 lg:px-6">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-bg-elevated transition-colors"
          >
            <Menu size={20} className="text-text-secondary" />
          </button>

          {/* Breadcrumb / Page Title */}
          <div className="hidden lg:block">
            <h1 className="text-lg font-semibold text-text-primary capitalize">
              {location.pathname.split('/').pop()?.replace(/-/g, ' ') || 'Dashboard'}
            </h1>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <button className="relative flex items-center justify-center w-10 h-10 rounded-lg hover:bg-bg-elevated transition-colors">
              <Bell size={20} className="text-text-secondary" />
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500" />
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-bg-elevated transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                  {session.user.email.charAt(0).toUpperCase()}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-text-primary">{session.user.name}</p>
                  <p className="text-xs text-text-tertiary">{session.user.role.replace('_', ' ')}</p>
                </div>
                <ChevronDown size={16} className="text-text-tertiary" />
              </button>

              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-56 bg-bg-card border border-border-default rounded-lg shadow-lg z-50 overflow-hidden">
                    <div className="p-3 border-b border-border-default">
                      <p className="font-medium text-text-primary">{session.user.email}</p>
                      <p className="text-xs text-text-tertiary capitalize">{session.user.role.replace('_', ' ')}</p>
                    </div>
                    <div className="p-1">
                      <Link
                        to="/admin/settings"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-lg transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings size={16} />
                        Account Settings
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Protected Route Wrapper
export function RequireAdminAuth({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, checkSession } = useAdminAuth();

  useEffect(() => {
    if (!isAuthenticated || !checkSession()) {
      navigate('/admin/login', { state: { from: location.pathname }, replace: true });
    }
  }, [isAuthenticated, checkSession, navigate, location.pathname]);

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
